import React, { ChangeEvent, FC, useCallback, useEffect, useState } from "react";
import classnames from "classnames";
import { useSelector } from "react-redux";
import { CoreValue, GivingActivity, SuggestedCollaborator } from "@shared/models";
import { Option } from "@shared/interfaces";
import { AvatarImage, Button, getInitials, Textarea } from "@shared/components";
import { getFullName } from "@shared/utils";
import { selectors as sharedSelectors } from "@shared/store/";
import { CreateRecognitionDto, RecipientOption, RecognitionDrawerValues } from "@containers/Recognition/interfaces";

// Relative, not the "@containers" alias: jest resolves no path aliases (no `jest` key in package.json,
// confirmed against every other virtual-mocked boundary in this feature), and this file's own test
// (RecognitionForm.test.tsx) imports the REAL formHelpers module rather than mocking it away -- same
// resolution constraint LeaderboardBoardToggle.tsx's own relative import of PillToggle documents. The
// old dropdown form imported this same sibling file via the alias; nothing ever exercised that path
// under jest (no RecognitionForm.test.tsx existed), which is why it went unnoticed until now.
import {
  coreValueGlyphLetter,
  getInitValues,
  prepareSubmitValue,
  RECOGNITION_SCORE_OPTIONS,
  recipientFirstName,
  recipientInitials,
} from "./formHelpers";
import RecipientSearchPicker from "./RecipientSearchPicker";

import "./index.scss";

interface RecognitionFormProps {
  onChangeForm: () => void;
  onSubmitForm: (payload: CreateRecognitionDto) => void;
  // 923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §4 S3: optional prefill for the "To" row -- see formHelpers.tsx's own
  // getInitValues comment. OPTIONAL; every existing caller that omits it must keep getting a blank picker.
  initialUser?: Option;
  // 923-core-context context/engineering/spark-rewards/phase-54-give-recognition-drawer.md: the "For" section's five always-visible rows -- fetched once by
  // RecognitionCreateContainer (the container) on mount, passed down here as plain data. Presentational:
  // this component never dispatches or selects on its own.
  coreValues: CoreValue[];
  // The SAME response the "Worth" budget readout and the "Why" counter/submit-gating bounds all read --
  // one object so the three can never describe two different fetches. `null` while loading/before the
  // container's own fetch resolves.
  givingActivity: GivingActivity | null;
  // pavel: "your implementation doesn't match the design. I highlighted empty state in Figma" (frame
  // 9198:1162) -- the "To" section's empty-state suggestion chips, the SAME Harvest-derived list
  // SparkSpreadSection already renders as full cards on the Leaderboard tab. Fetched by
  // RecognitionCreateContainer alongside coreValues/givingActivity above, same presentational split:
  // this component never dispatches or selects on its own. `null` before the container's own fetch
  // resolves -- see `suggestedCollaboratorsIsLoaded` below for why that state renders no block at all,
  // not an empty one.
  suggestedCollaborators: SuggestedCollaborator[] | null;
  // Gates the whole suggested block off until the fetch has actually settled -- rendering the caption
  // with zero chips underneath (the first frame, before the request resolves, if this weren't checked)
  // is explicitly worse than rendering nothing (pavel's own framing in the design note).
  suggestedCollaboratorsIsLoaded: boolean;
  // 923-core-context context/engineering/spark-rewards/phase-56-drawer-follow-ups.md #1: the container's own createRecognition loader, so Send is also
  // disabled for the in-flight window a click can't otherwise see (canSubmit alone doesn't know a
  // request is already out). Optional -- every existing caller/test that doesn't exercise submission
  // state keeps Send gated on canSubmit alone.
  isSubmitting?: boolean;
}

// The frame (9199:1162) shows 3 chips; the api itself returns up to 4 (SuggestedCollaboratorsResponse's
// own model comment). This cap is the DESIGN's, not the api's -- slicing here keeps that a property of
// this component rather than an unstated assumption about the response shape.
const MAX_SUGGESTED_CHIPS = 3;

/**
 * 923-core-context context/engineering/spark-rewards/phase-54-give-recognition-drawer.md: the drawer's body (the four "To"/"For"/"Worth"/"Why" sections,
 * the scrolling element inside the shell) and footer, rendered as a fragment so both land as DIRECT flex
 * children of `RecognitionCreateContainer`'s `.recognition-drawer` shell -- that shell's own `gap: 20px`
 * is what spaces the header/body/footer apart, and a fragment adds no wrapping DOM node to break that.
 *
 * Plain `useState`: the core-value/score pickers are always-visible rows/buttons with no schema-driven
 * validation of their own, and the note's bounds come from the server (`givingActivity.noteMinLength`/
 * `noteMaxLength`), which a validation schema built once at mount could not re-derive once that fetch
 * resolves. Submission is gated by a plain `canSubmit` boolean.
 *
 * `onChangeForm` is called on every user-driven change (never on the `initialUser` reset effect
 * below) -- RecognitionCreateContainer's own `changeFormValues` marks the overlay `leaveConfirm: true`
 * from it.
 */
const RecognitionForm: FC<RecognitionFormProps> = (props) => {
  const {
    onChangeForm,
    onSubmitForm,
    initialUser,
    coreValues,
    givingActivity,
    suggestedCollaborators,
    suggestedCollaboratorsIsLoaded,
    isSubmitting,
  } = props;
  const currentUser = useSelector(sharedSelectors.getUserDetails());

  const [values, setValues] = useState<RecognitionDrawerValues>(() => getInitValues(initialUser));

  // The "To" field's live search text -- lifted out of RecipientSearchPicker (which is otherwise a
  // controlled combobox with no state of its own) so THIS component can gate the empty-state suggestion
  // chips off it: the chips must disappear the instant the person starts typing (frame 9267:1668), and
  // that gate lives here, next to `suggestedChips`, not inside the picker.
  const [recipientQuery, setRecipientQuery] = useState("");

  // A later change to `initialUser` (a new overlay prop) resets the WHOLE drawer back to a fresh
  // blank/prefilled state. Deliberately does NOT call `onChangeForm` -- the reset is not a user-driven
  // edit.
  useEffect(() => {
    setValues(getInitValues(initialUser));
  }, [initialUser]);

  const selectRecipient = useCallback(
    (option: RecipientOption | null) => {
      setValues((prev) => ({ ...prev, user: option }));
      // Clears whatever was typed to find them -- reopening the picker later (after a `clearRecipient`)
      // must start from the empty state, not resume mid-search.
      setRecipientQuery("");
      onChangeForm();
    },
    [onChangeForm],
  );

  const clearRecipient = useCallback(() => {
    setValues((prev) => ({ ...prev, user: null }));
    setRecipientQuery("");
    onChangeForm();
  }, [onChangeForm]);

  const selectCoreValue = useCallback(
    (coreValue: CoreValue) => {
      setValues((prev) => ({ ...prev, core_value: coreValue }));
      onChangeForm();
    },
    [onChangeForm],
  );

  const selectScore = useCallback(
    (score: number) => {
      setValues((prev) => ({ ...prev, score }));
      onChangeForm();
    },
    [onChangeForm],
  );

  const changeDescription = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const description = event.target.value;
      setValues((prev) => ({ ...prev, description }));
      onChangeForm();
    },
    [onChangeForm],
  );

  const handleSubmit = useCallback(() => {
    onSubmitForm(prepareSubmitValue(values));
  }, [onSubmitForm, values]);

  // `givingActivity` gates the note bounds -- while it hasn't loaded (or the paired api change hasn't
  // shipped `noteMinLength`/`noteMaxLength` yet, see GivingActivity.tsx's own comment), both comparisons
  // below are against `undefined` and evaluate false, so submission stays blocked rather than accepting
  // an out-of-contract note against a guessed fallback.
  const canSubmit = Boolean(
    values.user &&
    values.core_value &&
    values.score &&
    givingActivity &&
    values.description.trim().length >= givingActivity.noteMinLength &&
    values.description.length <= givingActivity.noteMaxLength,
  );

  const remainingBudget = givingActivity ? givingActivity.budget - givingActivity.usedTrailing7Days : null;
  const firstName = values.user ? recipientFirstName(values.user.label) : "";

  // Not loaded yet (`null`) or loaded-and-empty both collapse to the same empty array here -- the render
  // below gates the WHOLE block on `.length > 0`, so neither state ever paints a caption with nothing
  // under it. Capped to the design's 3, not the api's own 4 (MAX_SUGGESTED_CHIPS's own comment above).
  const suggestedChips = suggestedCollaboratorsIsLoaded
    ? (suggestedCollaborators ?? []).slice(0, MAX_SUGGESTED_CHIPS)
    : [];

  // Builds the exact same Option shape `handlers.user.prepareOptionFunction` and SparkSpreadSection's own
  // `handleRecognise` already build from a LeaderboardUser row -- clicking a chip is deliberately
  // INDISTINGUISHABLE, downstream, from picking that same person out of the search picker.
  const selectSuggestedCollaborator = useCallback(
    (collaborator: SuggestedCollaborator) => {
      selectRecipient({
        label: getFullName(collaborator.user),
        value: String(collaborator.user_id),
        avatarUrl: collaborator.user.avatar_url,
      });
    },
    [selectRecipient],
  );

  return (
    <>
      <div className="recognition-drawer-body" data-testid="recognition-drawer-body">
        <div className="recognition-drawer-section">
          <span className="recognition-drawer-section-label">To</span>
          {values.user ? (
            <div className="recognition-drawer-recipient-row" data-testid="recognition-drawer-recipient-row">
              <div className="recognition-drawer-recipient-avatar">
                <AvatarImage imageUrl={values.user.avatarUrl} fallback={recipientInitials(values.user.label)} />
              </div>
              <span className="recognition-drawer-recipient-name">{values.user.label}</span>
              <button
                type="button"
                className="recognition-drawer-recipient-clear"
                onClick={clearRecipient}
                data-testid="recognition-drawer-recipient-clear"
                aria-label="Clear recipient"
              >
                ✕
              </button>
            </div>
          ) : (
            <>
              <RecipientSearchPicker
                query={recipientQuery}
                onQueryChange={setRecipientQuery}
                onSelect={selectRecipient}
                excludedUserIds={currentUser ? [String(currentUser.id)] : []}
              />
              {/* Empty-state only -- gone the instant a recipient is chosen (this whole branch), OR the
                  instant the person starts typing (frame 9267:1668: the search-results list replaces
                  the chips while searching, never stacks with them). `suggestedChips` is already `[]`
                  while unloaded or genuinely empty, so the length check is the other gate for "render
                  nothing" (pavel's design note). */}
              {recipientQuery.trim().length === 0 && suggestedChips.length > 0 ? (
                <div className="recognition-drawer-suggested" data-testid="recognition-drawer-suggested">
                  <span className="recognition-drawer-suggested-caption">
                    You haven&apos;t recognised these teammates this quarter
                  </span>
                  <div className="recognition-drawer-suggested-chips">
                    {suggestedChips.map((collaborator) => (
                      <button
                        type="button"
                        key={collaborator.user_id}
                        className="recognition-drawer-suggested-chip"
                        onClick={() => selectSuggestedCollaborator(collaborator)}
                        data-testid={`recognition-drawer-suggested-chip-${collaborator.user_id}`}
                      >
                        <span className="recognition-drawer-suggested-chip-avatar">
                          <AvatarImage
                            imageUrl={collaborator.user.avatar_url}
                            fallback={getInitials(collaborator.user.first_name, collaborator.user.last_name)}
                          />
                        </span>
                        {/* FIRST NAME ONLY (frame 9199:1163) -- never the full name a chosen recipient
                            row shows. */}
                        <span className="recognition-drawer-suggested-chip-name">{collaborator.user.first_name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="recognition-drawer-section">
          <span className="recognition-drawer-section-label">For</span>
          <div className="recognition-drawer-value-rows">
            {coreValues.map((coreValue) => {
              const selected = values.core_value?.id === coreValue.id;
              return (
                <button
                  type="button"
                  key={coreValue.id}
                  className={classnames("recognition-drawer-value-row", {
                    "recognition-drawer-value-row--selected": selected,
                  })}
                  onClick={() => selectCoreValue(coreValue)}
                  data-testid={`recognition-drawer-value-row-${coreValue.id}`}
                  aria-pressed={selected}
                >
                  <span className="recognition-drawer-value-glyph">{coreValueGlyphLetter(coreValue.title)}</span>
                  <span className="recognition-drawer-value-title">{coreValue.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="recognition-drawer-section">
          <div className="recognition-drawer-section-head">
            <span className="recognition-drawer-section-label">Worth</span>
            {remainingBudget !== null && givingActivity ? (
              <span className="recognition-drawer-section-caption" data-testid="recognition-drawer-budget-caption">
                {`${remainingBudget} of ${givingActivity.budget} left this week`}
              </span>
            ) : null}
          </div>
          <div className="recognition-drawer-score-buttons">
            {RECOGNITION_SCORE_OPTIONS.map((option) => {
              const selected = values.score === option.value;
              return (
                <button
                  type="button"
                  key={option.value}
                  className={classnames("recognition-drawer-score-button", {
                    "recognition-drawer-score-button--selected": selected,
                  })}
                  onClick={() => selectScore(option.value)}
                  data-testid={`recognition-drawer-score-button-${option.value}`}
                  aria-pressed={selected}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="recognition-drawer-section">
          <div className="recognition-drawer-section-head">
            <span className="recognition-drawer-section-label">Why</span>
            <span className="recognition-drawer-section-caption" data-testid="recognition-drawer-counter">
              {/* Omits "/ max" until givingActivity has loaded (or if its request failed) -- rendering
                  "0 / –" stated a max that does not exist yet, rather than simply not stating one. */}
              {givingActivity
                ? `Required · ${values.description.length} / ${givingActivity.noteMaxLength}`
                : `Required · ${values.description.length}`}
            </span>
          </div>
          <Textarea
            className="recognition-drawer-note"
            data-testid="recognition-drawer-description"
            onChange={changeDescription}
            value={values.description}
            maxLength={givingActivity?.noteMaxLength}
            placeholder="Stayed late to unblock the release and walked me through the fix"
          />
        </div>
      </div>

      <div className="recognition-drawer-footer">
        <span className="recognition-drawer-footer-caption">
          {firstName
            ? `Sparks land pending HR approval. ${firstName} sees it once approved.`
            : "Sparks land pending HR approval. They see it once approved."}
        </span>
        <Button
          type="button"
          className="recognition-drawer-submit"
          onClick={handleSubmit}
          disabled={!canSubmit || Boolean(isSubmitting)}
          data-testid="recognition-drawer-submit"
        >
          Send recognition
        </Button>
      </div>
    </>
  );
};

export default RecognitionForm;
